/** Shared labels / colors for similarity relations. */

export const RELATION_ORDER = ['EQUIVALENT_TO', 'INCLUDED_INTO', 'OVERLAPS'];

export const RELATION_LABELS = {
    EQUIVALENT_TO: 'EQUIVALENT TO',
    INCLUDED_INTO: 'INCLUDED INTO',
    OVERLAPS: 'OVERLAPS',
};

export const RELATION_COLORS = {
    EQUIVALENT_TO: '#1565c0',
    INCLUDED_INTO: '#6a1b9a',
    OVERLAPS: '#ef6c00',
};

export const TYPE_COLORS = {
    ICR: '#1565c0',
    EEI: '#00838f',
    GAP: '#6d4c41',
};

export function formatRelation(relation) {
    return RELATION_LABELS[relation] || relation || '—';
}

// ── FULL / PARTIAL display mapping (contract C3) ────────────────────────────

export const DISPLAY_RELATION_ORDER = ['FULL', 'PARTIAL'];

export const DISPLAY_RELATION_LABELS = {
    FULL: 'FULL',
    PARTIAL: 'PARTIAL',
};

export const DISPLAY_RELATION_COLORS = {
    FULL: '#1565c0',
    PARTIAL: '#ef6c00',
};

const RAW_TO_DISPLAY_RELATION = {
    EQUIVALENT_TO: 'FULL',
    OVERLAPS: 'PARTIAL',
    INCLUDED_INTO: 'PARTIAL',
    INCLUDES: 'PARTIAL',
};

/** FULL/PARTIAL for an edge: API field first, raw relation fallback. */
export function edgeDisplayRelation(edge) {
    if (!edge) return null;
    if (edge.displayRelation === 'FULL' || edge.displayRelation === 'PARTIAL') {
        return edge.displayRelation;
    }
    return RAW_TO_DISPLAY_RELATION[edge.relation] || null;
}

/** solid (manual or confirmed) vs dashed (unconfirmed machine). */
export function edgeLineStyle(edge) {
    if (!edge) return 'dashed';
    if (edge.lineStyle === 'solid' || edge.lineStyle === 'dashed') {
        return edge.lineStyle;
    }
    if (edge.source === 'manual' || edge.reviewStatus === 'confirmed') {
        return 'solid';
    }
    return 'dashed';
}

/** Percent label drawn over the edge — hidden on FULL edges. */
export function edgeDisplayScoreLabel(edge) {
    if (!edge) return null;
    if (edgeDisplayRelation(edge) === 'FULL') return null;
    const score = edge.displayScore != null ? edge.displayScore : edge.score;
    return formatSimilarityScorePct(score);
}

/** Normalize a stored score (0–1 or 0–100) to an integer percent 0–100. */
export function normalizeSimilarityPercent(score) {
    if (score == null || Number.isNaN(Number(score))) return null;
    const n = Number(score);
    return Math.max(0, Math.min(100, n <= 1 ? Math.round(n * 100) : Math.round(n)));
}

/**
 * Similarity percent for list rows. Uses stored score when present;
 * FULL / manual / proposed edges without a score are treated as 100%.
 */
export function edgeSimilarityPercent(edge) {
    if (!edge) return 0;
    const fromScore = normalizeSimilarityPercent(
        edge.displayScore != null ? edge.displayScore : edge.score,
    );
    if (fromScore != null) return fromScore;
    if (edgeDisplayRelation(edge) === 'FULL') return 100;
    if (edge.source === 'proposed' || edge.source === 'manual') return 100;
    return 0;
}

/** Number of filled bars (0–5) for a similarity percent; each bar is 20%. */
export function similarityFilledBarCount(percent) {
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    if (pct <= 0) return 0;
    return Math.min(5, Math.ceil(pct / 20));
}

/** Bar color from filled count: 1–2 red, 3 orange, 4 yellow, 5 green. */
export function similarityBarColor(filledCount) {
    if (filledCount <= 2) return '#d32f2f';
    if (filledCount === 3) return '#ed6c02';
    if (filledCount === 4) return '#fbc02d';
    return '#2e7d32';
}

export function coverageBadge(coverage) {
    if (!coverage) return {label: '—', score: null};
    const score = coverage.overallScore;
    if (score == null) {
        return {label: coverage.label || 'Uncovered', score: null};
    }
    return {label: `${coverage.label || 'Coverage'} · ${score}%`, score};
}

/** Chip color for a 0–100 coverage percentage: red < 10, green > 90, else yellow. */
export function coverageScoreTone(score) {
    if (score == null || Number.isNaN(Number(score))) return 'default';
    const n = Number(score);
    if (n < 10) return 'error';
    if (n > 90) return 'success';
    return 'warning';
}

/**
 * Data + Analytics coverage chips for ICR/EEI/Gap targets.
 * Analytics uses overallScore; Data uses dataScore. Empty when scores are absent.
 * `type` is accepted for call-site compatibility and ignored.
 */
export function coveragePairBadges(coverage, type) {
    void type;
    if (!coverage) return [];
    const analytic = coverage.overallScore;
    const data = coverage.dataScore;
    const badges = [];
    if (analytic != null && !Number.isNaN(Number(analytic))) {
        const n = Number(analytic);
        badges.push({
            key: 'analytics',
            label: `Analytics: ${Math.round(n)}%`,
            tone: coverageScoreTone(n),
        });
    }
    if (data != null && !Number.isNaN(Number(data))) {
        const n = Number(data);
        badges.push({key: 'data', label: `Data: ${Math.round(n)}%`, tone: coverageScoreTone(n)});
    }
    return badges;
}

/**
 * Normalize coverage from a gap list item: prefer nested `coverage`, else
 * `analyticCoveragePct` / `dataCoveragePct` (tag browse / list shape).
 */
export function resolveEntityCoverage(entity) {
    if (!entity) return null;
    if (entity.coverage) return entity.coverage;
    if (entity.analyticCoveragePct != null || entity.dataCoveragePct != null) {
        return {
            overallScore: entity.analyticCoveragePct ?? null,
            dataScore: entity.dataCoveragePct ?? null,
        };
    }
    return null;
}

/** Row badge: FULL or PARTIAL (score shown separately on hover). */
export function relationRowBadgeLabel(edge) {
    const rel = edgeDisplayRelation(edge);
    if (rel === 'FULL' || rel === 'PARTIAL') return rel;
    return rel || '—';
}

/**
 * Format a similarity / overlap score as a percentage string.
 * Scores are stored as 0–1 (LLM meaning or cosine) or already 0–100.
 */
export function formatSimilarityScorePct(score) {
    if (score == null || Number.isNaN(Number(score))) return null;
    const n = Number(score);
    const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
    return `${pct}%`;
}

/** Human-readable GAP-YYYY-NNNNN when present on a gap target/node. */
export function gapDisplayNumber(entity) {
    if (!entity) return null;
    return entity.number || entity.gapNumber || null;
}

export function targetTitle(target) {
    if (!target) return '—';
    if (target.type === 'GAP') {
        const num = gapDisplayNumber(target);
        const text = target.text || target.title || '';
        return num ? `${num} — ${text}` : (text || 'Gap');
    }
    if (target.type === 'EEI') {
        const num = target.number || target.eeiNumber;
        return num ? `${num} — ${target.text || target.title || ''}` : (target.text || target.title || 'EEI');
    }
    const num = target.number || target.icrNumber;
    const body = target.question || target.title || target.text || 'ICR';
    return num ? `${num} — ${body}` : body;
}

/** Split a target into display number + question text (matches Gaps card layout). */
export function splitTargetTitle(target) {
    const full = targetTitle(target);
    if (!target) return {number: null, text: full};

    let number = null;
    if (target.type === 'GAP') {
        number = gapDisplayNumber(target);
    } else if (target.type === 'EEI') {
        number = target.number || target.eeiNumber || null;
    } else if (target.type === 'ICR') {
        number = target.number || target.icrNumber || null;
    }

    if (number) {
        const prefix = `${number} — `;
        if (full.startsWith(prefix)) {
            return {number, text: full.slice(prefix.length)};
        }
    }
    return {number: null, text: full};
}

export function nodeKey(type, id) {
    return `${type}:${id}`;
}

export function resolveIcrNavPath(target) {
    if (!target || target.type !== 'ICR') return null;
    const key = target.number || target.id;
    if (!key) return null;
    return `/icr/${encodeURIComponent(key)}`;
}

export function resolveEeiNavPath(target) {
    if (!target || target.type !== 'EEI') return null;
    const key = target.number || target.id;
    if (!key) return null;
    return `/icr/eei/${encodeURIComponent(key)}`;
}

/**
 * Dedicated Gap view page (Requirements Gaps detail).
 */
export function resolveGapNavPath(target) {
    if (!target || target.type !== 'GAP') return null;
    const key = gapDisplayNumber(target) || target.id;
    if (!key) return null;
    return `/icr/gap/${encodeURIComponent(key)}`;
}

/**
 * FINTEL product URL for a Gap, opening the Gaps tab and focusing the gap.
 * Query keys match FINTEL_SEARCH_PARAM_KEYS in previewPage/delta/deltaSearchParams.
 * Prefer {@link resolveGapNavPath} for primary navigation from Requirements.
 */
export function resolveGapFintelPath(target) {
    if (!target || target.type !== 'GAP') return null;
    if (!target.reportId) return null;
    const params = new URLSearchParams();
    params.set('fintelTab', 'gaps');
    const gapKey = gapDisplayNumber(target) || target.id;
    if (gapKey) params.set('gap', String(gapKey));
    return `/products/${encodeURIComponent(target.reportId)}?${params.toString()}`;
}
